<script setup>
import { ref, computed, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import Banner from 'dashboard/components/ui/Banner.vue';

// Aceleres: aviso global de instabilidade Meta/WhatsApp.
// Estado vem do InstallationConfig ACELERES_META_NOTICE. Render inicial sem
// flash via window.globalConfig; polling de 60s reflete liga/desliga sem reload.
const store = useStore();

const DISMISS_KEY = 'aceleres_meta_notice_dismissed_at';
const POLL_MS = 60000;

const initial =
  (window.globalConfig && window.globalConfig.ACELERES_META_NOTICE) || {};
const enabled = ref(!!initial.enabled);
const message = ref(initial.message || '');
const updatedAt = ref(Number(initial.updated_at) || 0);
const dismissedAt = ref(localStorage.getItem(DISMISS_KEY) || '');

let pollTimer = null;

const accountId = computed(() => store.getters.getCurrentAccountId);
const currentUser = computed(() => store.getters['auth/getCurrentUser']);
const accessToken = computed(() => currentUser.value?.access_token || '');
const canFetchNotice = computed(() => !!accountId.value);

// Mostra só se ligado, com texto, e ainda não dispensado para este updated_at.
// Se o admin reativar ou editar o texto (novo updated_at), o aviso reaparece.
const shouldShow = computed(
  () =>
    !!accountId.value &&
    enabled.value &&
    !!message.value &&
    String(dismissedAt.value) !== String(updatedAt.value)
);

const fetchNotice = async () => {
  if (!canFetchNotice.value) return;

  const id = accountId.value;
  try {
    // Fallback: se o access_token do store estiver vazio (ex: chamada antes do
    // profile hidratar), usa os headers devise do cookie de sessao do proprio app.
    const headers = accessToken.value
      ? { api_access_token: accessToken.value }
      : (() => {
          try {
            const m = document.cookie.match(
              /(?:^|;\s*)cw_d_session_info=([^;]+)/
            );
            const d = m ? JSON.parse(decodeURIComponent(m[1])) : {};
            return d['access-token']
              ? {
                  'access-token': d['access-token'],
                  'token-type': d['token-type'] || 'Bearer',
                  client: d.client || '',
                  expiry: String(d.expiry || ''),
                  uid: d.uid || '',
                }
              : {};
          } catch (e) {
            return {};
          }
        })();
    const { data } = await axios.get(
      `/api/v1/accounts/${id}/aceleres/meta_notice`,
      { headers }
    );
    enabled.value = !!data.enabled;
    message.value = data.message || '';
    updatedAt.value = Number(data.updated_at) || 0;
  } catch (e) {
    // Silencioso — mantém o último estado conhecido
  }
};

const dismiss = () => {
  dismissedAt.value = String(updatedAt.value);
  localStorage.setItem(DISMISS_KEY, dismissedAt.value);
};

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

const startPolling = () => {
  stopPolling();
  fetchNotice();
  pollTimer = setInterval(fetchNotice, POLL_MS);
};

watch(
  [accountId, accessToken],
  ([id, token]) => {
    if (!id || !token) {
      stopPolling();
      return;
    }

    startPolling();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  stopPolling();
});
</script>

<template>
  <Banner
    v-if="shouldShow"
    color-scheme="warning"
    :banner-message="message"
    has-close-button
    @close="dismiss"
  />
</template>
